import { DefaultRequestBody, MockedRequest, rest, RestHandler } from 'msw';
import { CreateInternalCommentRequest } from '../../queries/use-create-internal-comment';
import { CreateInternalCommentReplyRequest } from '../../queries/use-create-internal-comment-reply';
import { UpdateInternalCommentReplyRequest } from '../../queries/use-update-internal-comment-reply';
import { UpdateInternalCommentsRequest } from '../../queries/use-update-internal-comments';
import { PostResponseBody } from '../../types/api-response';
import { InternalComment } from '../../types/internal-comment';
import { OtherSchedule, OtherScheduleStatusEnum } from '../../types/other-schedule';
import { Reply } from '../../types/reply';
import { ReconciliationWorksheet, WorksheetStatusEnum } from '../../types/worksheet';
import { parseDate } from '../../utilities/formatting';
import { DB } from '../db';

const routes: RestHandler<MockedRequest<DefaultRequestBody>>[] = [];

routes.push(
    /**
     * GET /workpapers/:id/internal-comments
     * Gets all internal comments for a workpaper
     */
    rest.get('/workpapers/:workpaperId/internal-comments', (req, res, ctx) => {
        const page = parseInt(req.url.searchParams.get('page') || '1');
        const limit = parseInt(req.url.searchParams.get('limit') || '10');
        const search = req.url.searchParams.get('search');
        const sort = req.url.searchParams.get('sort');
        const filter = req.url.searchParams.get('filter');

        const workpaperId = req.params.workpaperId as string;
        let internalComments = DB.GetInternalComments(workpaperId);
        let totalItems = internalComments.length;

        if (search) {
            internalComments = internalComments.filter((ic) => ic.rawComment.toLowerCase().includes(search.toLowerCase()));
        }
        if (filter) {
            const filterObj = JSON.parse(decodeURIComponent(filter));
            if (filterObj.type) {
                const value = filterObj.type;
                internalComments = internalComments.filter((ic) => {
                    let found = false;
                    if (value.includes('GENERAL') && !ic.worksheet) {
                        found = true;
                    } else if (value.includes('WORKSHEETS') && ic.worksheet !== undefined) {
                        found = true;
                    }
                    return found;
                });
            }

            if (filterObj.context) {
                internalComments = internalComments.filter((ic) => filterObj.context.includes(ic.worksheet?.id));
            }
            if (filterObj.updatedAt) {
                const { base, condition, to } = filterObj.updatedAt;
                if (condition === '>') {
                    internalComments = internalComments.filter((ic) => new Date(parseDate(ic.updatedAt)) > new Date(base));
                } else if (condition === '=') {
                    internalComments = internalComments.filter((ic) => parseDate(ic.updatedAt) === base);
                } else if (condition === '<') {
                    internalComments = internalComments.filter((ic) => new Date(parseDate(ic.updatedAt)) < new Date(base));
                } else {
                    internalComments = internalComments.filter(
                        (ic) => new Date(parseDate(ic.updatedAt)) >= new Date(base) && new Date(parseDate(ic.updatedAt)) <= new Date(to)
                    );
                }
            }
            if (filterObj.replyCount) {
                const { base, condition, to } = filterObj.replyCount;
                if (condition === '>') {
                    internalComments = internalComments.filter((ic) => ic.replies.length > base);
                } else if (condition === '=') {
                    internalComments = internalComments.filter((ic) => ic.replies.length === base);
                } else if (condition === '<') {
                    internalComments = internalComments.filter((ic) => ic.replies.length < base);
                } else {
                    internalComments = internalComments.filter((ic) => ic.replies.length >= base && ic.replies.length <= to);
                }
            }

            if (filterObj.author) {
                const value = filterObj.author;
                internalComments = internalComments.filter((ic) => value.includes(ic.createdBy.id));
            }
            totalItems = internalComments.length;
        }
        if (sort) {
            const { order, option } = JSON.parse(decodeURIComponent(sort));

            if (option === 'Last Updated') {
                internalComments = internalComments.sort((a, b) => (a.updatedAt > b.updatedAt ? 1 : -1));
            } else if (option === 'Status') {
                internalComments = internalComments.sort((a, b) => (a.status > b.status ? 1 : -1));
            } else if (option === 'Reply Count') {
                internalComments = internalComments.sort((a, b) => (a.replies.length > b.replies.length ? 1 : -1));
            }
            if (order === 'desc') {
                internalComments = internalComments.reverse();
            }
        }

        return res(ctx.delay(), ctx.status(200), ctx.json({ data: internalComments, totalItems: totalItems }));
    }),
    /**
     * GET /workpapers/:workpaperId/internal-comments/:id
     * Gets an internal comment for a workpaper
     */
    rest.get('/workpapers/:workpaperId/internal-comments/:id', (req, res, ctx) => {
        const id = req.params.id as string;
        const internalComment = DB.GetInternalComment(id);

        return res(ctx.delay(), ctx.status(200), ctx.json({ internalComment }));
    }),
    /**
     * DELETE /workpapers/:workpaperId/internal-comments/:id
     * Deletes an internal comment for a workpaper
     */
    rest.delete('/workpapers/:workpaperId/internal-comments/:id', (req, res, ctx) => {
        const id = req.params.id as string;

        DB.DeleteInternalComment(id);

        return res(ctx.delay(), ctx.status(204));
    }),
    /**
     * POST /workpapers/:workpaperId/internal-comments
     * Creates an internal comment for a workpaper
     */
    rest.post<CreateInternalCommentRequest>('/workpapers/:workpaperId/internal-comments', (req, res, ctx) => {
        const workpaperId = req.params.workpaperId as string;

        const response: PostResponseBody<InternalComment> = {
            hasErrors: false,
            results: [],
        };

        for (const { rawComment, worksheetId, otherScheduleId } of req.body.internalComments) {
            const internalComment = DB.CreateInternalComment(workpaperId, rawComment, worksheetId, otherScheduleId);
            response.results.push({ statusCode: 201, data: internalComment });
        }

        return res(ctx.delay(), ctx.status(response.hasErrors ? 400 : 200), ctx.json(response));
    }),
    /**
     * PATCH /workpapers/:workpaperId/internal-comments
     * Updates internal comments for a workpaper
     */
    rest.patch<UpdateInternalCommentsRequest>('/workpapers/:workpaperId/internal-comments', (req, res, ctx) => {
        for (const internalCommentReq of req.body) {
            const comment = DB.UpdateInternalComment(internalCommentReq.id, internalCommentReq.fields);

            if (!comment) {
                continue;
            }

            if (comment.worksheet) {
                const worksheet = DB.GetWorksheet(comment.worksheet.id) as ReconciliationWorksheet;
                if (worksheet.status === WorksheetStatusEnum.REVIEWED) {
                    DB.CalculateWorksheetStatus(worksheet.id, WorksheetStatusEnum.AWAITING_REVIEW);
                }
            }
        }

        return res(ctx.delay(), ctx.status(200), ctx.json({}));
    }),

    /**
     * POST /workpapers/:workpaperId/internal-comments/:id/replies
     * Create a reply for an internal comment
     */
    rest.post<CreateInternalCommentReplyRequest>('/workpapers/:workpaperId/internal-comments/:id/replies', (req, res, ctx) => {
        const internalCommentId = req.params.id as string;

        const response: PostResponseBody<Reply> = {
            hasErrors: false,
            results: [],
        };

        const internalCommentReply = DB.CreateInternalCommentReply(internalCommentId, req.body.reply.rawReply);
        response.results.push({ statusCode: 201, data: internalCommentReply });

        return res(ctx.delay(), ctx.status(response.hasErrors ? 400 : 200), ctx.json(response));
    }),

    /**
     * DELETE /workpapers/:workpaperId/internal-comments/:internalCommentId/replies/:id
     * Deletes a reply for an internal comment
     */
    rest.delete('/workpapers/:workpaperId/internal-comments/:internalCommentId/replies/:id', (req, res, ctx) => {
        const id = req.params.id as string;

        DB.DeleteInternalCommentReply(id);

        return res(ctx.delay(), ctx.status(204));
    }),

    /**
     * PATCH /workpapers/:workpaperId/internal-comments/:internalCommentId/replies/:id
     * Updates reply for an internal comment
     */
    rest.patch<UpdateInternalCommentReplyRequest>(
        '/workpapers/:workpaperId/internal-comments/:internalCommentId/replies/:id',
        (req, res, ctx) => {
            const commentId = req.params.internalCommentId as string;
            const id = req.params.id as string;
            const { rawReply } = req.body;

            DB.UpdateInternalCommentReply(commentId, id, rawReply);

            return res(ctx.delay(), ctx.status(200));
        }
    )
);

export { routes };
